import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ErrorMessage,
  FieldArray,
  Form,
  Formik,
  type FormikErrors
} from "formik";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { type Config, type Prize, StockStrategy } from "kadow-jetpack-model";
import * as Yup from "yup";
import NiceModal from "@ebay/nice-modal-react";

import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { KC_Sizes, KC_Weights } from "../../../../designsystem/ds_commons";
import { type StepProps } from "../../JetpackOnboarding";
import { tt } from "../../../../utils/i18n/i18n";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import GiftSelectorFooter from "./module/GiftSelectorFooter";
import { giftNameValidation } from "../../../19_JetpackConfig/components/GiftsConfiguration/helper/prize_validation_schema";
import { FIELD_REQUIRED } from "../../../../utils/constants";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import KC_Card_Dashed from "../../../../designsystem/KC_Card/KC_Card_Dashed";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";
import EditGiftModal from "./EditGiftModal";
import OnboardingAPI from "../../../03_Onboarding/helpers/OnboardingAPI";
import OnboardingGiftsCarousel from "./OnboardingGiftsCarousel";
import { CarouselAddPrizeButton } from "./CarouselAddPrizeButton";
import TSUtils from "../../../../utils/helpers/TSUtils";
import PrizeTemplateDTO from "../../../../model/PrizeTemplateDTO";
import "../styles.scss";

const validationSchema = Yup.object({
  prizes: Yup.array().of(
    Yup.object().shape({
      name: giftNameValidation,
      iQty: Yup.number().when("ss", {
        is: (ss: string) => ss !== StockStrategy.None,
        then: (schema) => schema.min(1, FIELD_REQUIRED).required(FIELD_REQUIRED)
      })
    })
  )
});

export default function GiftSelector04Onboarding(props: StepProps) {
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const [prizeTemplates, setPrizeTemplates] = useState<PrizeTemplateDTO[]>([]);

  const didDownloadPrizeTemplatesRef = useRef(false);

  const showCouponOptions = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.adc;
  }, [accountToDisplay?.saasFeatures?.adc]);

  const handleSubmit = (values: Config) => {
    const filteredValues = {
      ...values,
      prizes: values.prizes.filter((prize) => !prize.isTemplate)
    };

    dispatch(
      updateJetpackConfigThunk(
        filteredValues,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
    props.nextStep();
  };

  const editPrize = async (
    index: number,
    values: Config,
    errors: FormikErrors<Config>,
    touched?: any
  ) => {
    const initialPrize = values.prizes[index];

    if (!initialPrize) {
      alert(tt("error"));
      return;
    }

    const editedPrize: Prize | null = await NiceModal.show(EditGiftModal, {
      hasAdvancedCouponOptions: showCouponOptions,
      errors,
      touched,
      accountToDisplay,
      initialPrize,
      removeItem: () => {
        const updatedConfig = {
          ...values,
          prizes: [...values.prizes]
        };
        updatedConfig.prizes.splice(index, 1);
        dispatch(
          updateJetpackConfigThunk(
            updatedConfig,
            accountToDisplay?.trackingID,
            accountToDisplay?.token
          )
        );
      }
    });

    if (!editedPrize) {
      return;
    }

    const updatedConfig = {
      ...values,
      prizes: [...values.prizes]
    };
    updatedConfig.prizes[index] = editedPrize;

    dispatch(
      updateJetpackConfigThunk(
        updatedConfig,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const handleAddPrize = async (
    push: (prize: Prize) => void,
    values: Config,
    errors: FormikErrors<Config>,
    touched?: any
  ) => {
    if (!accountToDisplay) {
      return;
    }

    const prize: Prize | null = await NiceModal.show(EditGiftModal, {
      hasAdvancedCouponOptions: showCouponOptions,
      errors,
      touched,
      accountToDisplay
    });

    if (!prize) {
      return;
    }

    delete prize.isTemplate;

    push(prize);

    const updatedConfig = {
      ...values,
      prizes: [...(values.prizes || []), prize]
    };

    dispatch(
      updateJetpackConfigThunk(
        updatedConfig,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const renderAddGift = (className?: string) => {
    return (
      <div
        id="add-gift-div"
        className={`h-100 d-flex justify-content-center align-items-center gap-sm ${className || ""} add-gift-btn`}
      >
        <FontAwesomeIcon icon={faPlus} />
        <KC_UIText weight={KC_Weights.bold} text={tt("jp_add_gift_btn_text")} />
      </div>
    );
  };

  const validate = (values: Config) => {
    const errors: FormikErrors<Config> = {};
    if (values.stockStrategy !== StockStrategy.None) {
      return;
    }
    const total = Object.values(values.prizes)
      .filter((prize) => !prize.isTemplate)
      .reduce((sum, prize) => sum + Number(prize.pc), 0);

    if (total > 100) {
      Object.keys(values.prizes).forEach((_, index) => {
        // @ts-expect-error
        if (errors[`prizes_${index}_pc`]) return;
        // @ts-expect-error
        errors[`prizes_${index}_pc`] =
          "La somme des pourcentages ne doit pas dépasser 100%";
      });
    }

    return errors;
  };

  const getJetpackPrizeTemplates = async () => {
    if (!accountToDisplay) {
      return;
    }
    const templates =
      await OnboardingAPI.getJetpackPrizeTemplates(accountToDisplay);

    const templatesWithIds = templates.map((template) => ({
      ...template,
      id: TSUtils.generateRandomString(5)
    }));

    setPrizeTemplates(templatesWithIds);
  };

  useEffect(() => {
    if (!jetpackConfig || !accountToDisplay) {
      return;
    }

    if (didDownloadPrizeTemplatesRef.current) {
      return;
    }

    didDownloadPrizeTemplatesRef.current = true;
    void getJetpackPrizeTemplates();
  }, [jetpackConfig, accountToDisplay]);

  if (jetpackConfig == null) {
    return <div>Erreur</div>;
  }

  return (
    <Formik<Config>
      initialValues={jetpackConfig}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validate={validate}
      validateOnBlur={true}
      validateOnChange
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, errors, touched }) => {
        // Filter out templates that are already selected in prizes
        const availableTemplates = useMemo(() => {
          if (!prizeTemplates.length || !values.prizes) {
            return prizeTemplates;
          }

          const selectedTemplateIds = new Set(
            values.prizes.map((prize) => prize.tid).filter(Boolean)
          );

          return prizeTemplates.filter(
            (template) => !selectedTemplateIds.has(template.id)
          );
        }, [prizeTemplates, values.prizes]);

        const shouldShowEmptyState =
          !values.prizes?.length && !availableTemplates.length;

        return (
          <Form>
            <div className="d-flex flex-column" style={{ height: "100vh" }}>
              <div
                id="all-gifts-editor"
                className="d-flex flex-column flex-grow-1"
              >
                <FieldArray name="prizes">
                  {({ push }) => (
                    <>
                      {shouldShowEmptyState ? (
                        <div className="d-none d-sm-block mx-4">
                          <CarouselAddPrizeButton
                            onClick={() =>
                              handleAddPrize(push, values, errors, touched)
                            }
                          />
                        </div>
                      ) : (
                        <OnboardingGiftsCarousel
                          values={values}
                          availableTemplates={availableTemplates}
                          allPrizeTemplates={prizeTemplates}
                          setFieldValue={setFieldValue}
                          accountToDisplay={accountToDisplay}
                          setEditingIndex={(index) => {
                            if (index === null) {
                              alert(tt("error"));
                              return;
                            }
                            void editPrize(index, values, errors, touched);
                          }}
                          onAddPrize={() =>
                            void handleAddPrize(push, values, errors, touched)
                          }
                          errors={errors}
                          touched={touched}
                        />
                      )}
                      <KC_Separator size={KC_Sizes.m} />
                      <div className="d-block d-sm-none">
                        <KC_Card_Dashed
                          id="add-gift"
                          children={renderAddGift("unset")}
                          isGift={true}
                          className="add-gift w-100"
                          onClick={() =>
                            handleAddPrize(push, values, errors, touched)
                          }
                        />
                        <KC_Separator size={KC_Sizes.m} />
                      </div>
                    </>
                  )}
                </FieldArray>
                <div className="w-100 d-flex justify-content-center">
                  {!errors.prizes?.length && (
                    <ErrorMessage
                      className="error-message"
                      name="prizes"
                      component="span"
                    />
                  )}
                </div>
                <KC_Padder className="d-none d-md-flex" />
              </div>
              <GiftSelectorFooter values={values} className="position-sticky" />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

// TODO JNG review this function
export const computeTotalWinPc = (
  prizes: Prize[],
  distributionType?: StockStrategy
): number => {
  if (!prizes) {
    return 0;
  }
  let total = 0;
  if (distributionType === StockStrategy.None) {
    total = prizes.reduce((sum, item) => sum + item.pc, 0);
  } else {
    const totalPrizesQty = prizes.reduce(
      (sum, prize) => sum + (prize.iQty || 0),
      0
    );
    if (totalPrizesQty == 0) {
      total = 0;
    } else {
      total = prizes.reduce((sum, prize) => {
        return sum + (prize.iQty || 0) / totalPrizesQty;
      }, 0);
    }
  }

  return isNaN(total) ? 0 : total;
};
